import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Clock, Coins, TrendingUp, Zap } from 'lucide-react';
interface SlideSalesFocusProps {
  onContinue: () => void;
}
type Mode = 'today' | 'future';
const STAGES = [{
  label: 'Stage I',
  tagline: 'Pilot',
  revLabel: 'Low rev · Fast decision',
  dealSize: '€5–10K',
  timeToClose: '4–7 weeks',
  today: 85,
  future: 20
}, {
  label: 'Stage II',
  tagline: 'Expansion',
  revLabel: 'Mid rev · Considered',
  dealSize: '€25–60K',
  timeToClose: '3–6 months',
  today: 10,
  future: 30
}, {
  label: 'Stage III',
  tagline: 'Scale',
  revLabel: 'High rev · Strategic',
  dealSize: '€100K+',
  timeToClose: '4–8 months',
  today: 5,
  future: 45
}, {
  label: 'Stage IV',
  tagline: 'Enterprise',
  revLabel: 'High rev · Slow decision',
  dealSize: '€1M+',
  timeToClose: '12+ months',
  today: 0,
  future: 5
}] as const;
export function SlideSalesFocus({
  onContinue
}: SlideSalesFocusProps) {
  const [stage, setStage] = useState(0);
  const [mode, setMode] = useState<Mode>('today');
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 250);
    const t2 = setTimeout(() => setStage(2), 700);
    const t3 = setTimeout(() => setStage(3), 1200);
    const t4 = setTimeout(() => setStage(4), 1700);
    const t5 = setTimeout(() => setMode('future'), 2600);
    const t6 = setTimeout(() => setMode('today'), 4400);
    return () => {
      ;
      [t1, t2, t3, t4, t5, t6].forEach(clearTimeout);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-7 md:px-16 md:py-9 max-w-[1400px] mx-auto" data-id="element-703">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-704">
        <p className="text-[#6C2BD9] text-xs md:text-sm font-bold uppercase tracking-[0.2em]" data-id="element-705">
          Why we're raising
        </p>
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-slate-900 tracking-tight leading-tight mt-2" data-id="element-706">
          From <span className="text-[#6C2BD9]" data-id="element-707">pilot</span> to{' '}
          <span className="text-[#6C2BD9]" data-id="element-708">enterprise penetration</span>
        </h2>
        <p className="text-slate-600 text-base md:text-lg mt-2 max-w-3xl" data-id="element-709">
          Today we live off Stage I — fast revenue, but small. Investment gives
          us the liquidity to push Stage II clients into Stage III, where deals
          are 10× larger.
        </p>
      </motion.div>

      {/* Pilot → Enterprise arrow strip */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 ? 1 : 0
    }} className="mt-6 flex items-center gap-3" data-id="element-710">
        <span className="text-xs uppercase tracking-widest font-bold text-slate-500" data-id="element-711">
          Pilot
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-300 via-[#6C2BD9]/40 to-[#6C2BD9]" data-id="element-712" />
        <ArrowRight className="w-4 h-4 text-[#6C2BD9]" data-id="element-713" />
        <span className="text-xs uppercase tracking-widest font-bold text-[#6C2BD9]" data-id="element-714">
          Enterprise Penetration
        </span>
      </motion.div>

      {/* Mode toggle */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 10
    }} className="mt-5 flex items-center justify-center" data-id="element-715">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-full p-1 relative" data-id="element-716">
          <button onClick={() => setMode('today')} className={`relative z-10 px-5 md:px-6 py-2 rounded-full text-sm font-semibold transition-colors ${mode === 'today' ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-717">
            <span className="flex items-center gap-2" data-id="element-718">
              <Zap className="w-3.5 h-3.5" data-id="element-719" />
              Today · Survival mode
            </span>
          </button>
          <button onClick={() => setMode('future')} className={`relative z-10 px-5 md:px-6 py-2 rounded-full text-sm font-semibold transition-colors ${mode === 'future' ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-720">
            <span className="flex items-center gap-2" data-id="element-721">
              <TrendingUp className="w-3.5 h-3.5" data-id="element-722" />
              With investment · Growth mode
            </span>
          </button>
          <motion.div layout transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className="absolute top-1 bottom-1 rounded-full bg-[#6C2BD9] shadow-md" style={{
          left: mode === 'today' ? 4 : '50%',
          right: mode === 'today' ? '50%' : 4
        }} data-id="element-723" />
        </div>
      </motion.div>

      {/* 4-stage grid */}
      <div className="flex-1 grid grid-cols-4 gap-3 md:gap-4 mt-6" data-id="element-724">
        {STAGES.map((s, idx) => {
        const value = mode === 'today' ? s.today : s.future;
        const isPrimaryFocus = mode === 'today' && idx === 0 || mode === 'future' && (idx === 1 || idx === 2);
        return <motion.div key={s.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 20
        }} transition={{
          duration: 0.4,
          delay: idx * 0.08
        }} className={`relative rounded-2xl p-4 md:p-5 flex flex-col border transition-colors ${isPrimaryFocus ? 'bg-[#F5F0FF] border-[#6C2BD9]' : 'bg-white border-slate-200'}`} data-id="element-725">
              <div className="flex items-baseline justify-between" data-id="element-726">
                <div data-id="element-727">
                  <p className="text-[10px] md:text-[11px] uppercase tracking-widest font-bold text-slate-500" data-id="element-728">
                    {s.label}
                  </p>
                  <h4 className={`font-bold text-lg md:text-xl mt-0.5 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-900'}`} data-id="element-729">
                    {s.tagline}
                  </h4>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-730">
                  {idx === 0 ? 'I' : idx === 1 ? 'II' : idx === 2 ? 'III' : 'IV'}
                </span>
              </div>

              <div className="mt-4 flex items-end gap-1" data-id="element-731">
                <motion.span key={`${s.label}-${mode}`} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className={`text-4xl md:text-5xl font-bold tabular-nums tracking-tight ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-732">
                  {value}
                </motion.span>
                <span className={`text-xl md:text-2xl font-bold mb-1 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-733">
                  %
                </span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5" data-id="element-734">
                  of focus
                </span>
              </div>

              <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden" data-id="element-735">
                <motion.div className={`h-full rounded-full ${isPrimaryFocus ? 'bg-[#6C2BD9]' : 'bg-slate-400'}`} animate={{
              width: `${value}%`
            }} transition={{
              type: 'spring',
              stiffness: 120,
              damping: 20
            }} data-id="element-736" />
              </div>

              <p className="text-xs text-slate-500 mt-3 leading-snug" data-id="element-737">
                {s.revLabel}
              </p>

              <div className="mt-4 pt-3 border-t border-slate-200 space-y-1.5" data-id="element-738">
                <div className="flex items-center justify-between text-xs" data-id="element-739">
                  <span className="flex items-center gap-1.5 text-slate-500" data-id="element-740">
                    <Coins className="w-3 h-3" data-id="element-741" />
                    Order size
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-742">{s.dealSize}</span>
                </div>
                <div className="flex items-center justify-between text-xs" data-id="element-743">
                  <span className="flex items-center gap-1.5 text-slate-500" data-id="element-744">
                    <Clock className="w-3 h-3" data-id="element-745" />
                    Time to close
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-746">
                    {s.timeToClose}
                  </span>
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 10
    }} className="mt-5 rounded-2xl border border-[#E4D7FF] bg-[#F5F0FF] px-5 py-4" data-id="element-747">
        <AnimatePresence mode="wait" data-id="element-748">
          <motion.div key={mode} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.3
        }} className="flex items-start gap-3" data-id="element-749">
            <div className="w-8 h-8 rounded-lg bg-[#6C2BD9] text-white flex items-center justify-center flex-shrink-0" data-id="element-750">
              {mode === 'today' ? <Zap className="w-4 h-4" data-id="element-751" /> : <TrendingUp className="w-4 h-4" data-id="element-752" />}
            </div>
            <div className="flex-1" data-id="element-753">
              {mode === 'today' ? <>
                  <p className="text-sm font-bold text-slate-900" data-id="element-754">
                    Today, 85% of our sales effort goes to Stage I deals.
                  </p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-id="element-755">
                    Pilots close fast (4–7 weeks) and bring in cash, but they
                    cap at €5–10K. Out of necessity we chase what we can close —
                    leaving the bigger Stage II & III opportunities sitting in
                    the pipeline.
                  </p>
                </> : <>
                  <p className="text-sm font-bold text-slate-900" data-id="element-756">
                    With investment, 75% of effort shifts to Stage II & III.
                  </p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-id="element-757">
                    Liquidity lets us run 3–6 month sales cycles and convert
                    existing Stage II relationships into Stage III deals
                    (€100K+). Same accounts, 10× the deal size — and the path to
                    Stage IV enterprise penetration opens up.
                  </p>
                </>}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-4 flex justify-end" data-id="element-758">
        <Button onClick={onContinue} data-id="element-759">Fast Calculator</Button>
      </motion.div>
    </div>;
}